import PropTypes from 'prop-types';
import merge from 'lodash/merge';
import * as React from 'react';
// @mui
import { Card, CardHeader, Box } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import OutlinedInput from '@mui/material/OutlinedInput';
import TextField from '@mui/material/TextField';
import FormHelperText from '@mui/material/FormHelperText';

import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import InfoIcon from '@mui/icons-material/Info';
import IconButton from '@mui/material/IconButton';
import axios from 'axios';
// components
import { BaseOptionChart } from '../../../components/chart';

// ----------------------------------------------------------------------

AppGeneGenome.propTypes = {
  title: PropTypes.string,
  subheader: PropTypes.string,
  // chartData: PropTypes.array.isRequired,
  // chartLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default function AppGeneGenome({ title, subheader,GenomeToParent,GenesToParent,parentToChildGenes, parentToChildGenome, VariantSourceToParent, parentToChildVariantSource,  ...other }) {
  const chartOptions = merge(BaseOptionChart(), {
    plotOptions: { bar: { columnWidth: '16%' } },
    xaxis: { type: 'datetime' },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (y) => {
          if (typeof y !== 'undefined') {
            return `${y.toFixed(0)} visits`;
          }
          return y;
        },
      },
    },
  });

    const CustomWidthTooltip = styled(({ className, ...props }) => (
      <Tooltip {...props} classes={{ popper: className }} />
    ))({
      [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: 380,
      },
    });

    // Genome selection
    const [Genome, setGenome] = React.useState('GRCh38');
    const handleGenomeChange = (event: SelectChangeEvent) => {
      setGenome(event.target.value);
      GenomeToParent(event.target.value)
      // a variant set is built against one reference; carrying it across genomes is meaningless
      if (VariantSourceToParent) { VariantSourceToParent('') }
    };

    // Cell line / variant background.  The list comes from the backend rather than being
    // hard-coded here: which cell lines exist depends on which VCFs have been processed on
    // this deployment, and the sample names drift as new clones are sequenced.
    const [VariantSets, setVariantSets] = React.useState([]);
    React.useEffect(() => {
      let cancelled = false;
      const params = { genome_ver: parentToChildGenome };
      // '/api/' is the canonical route, but it is a new prefix: a deployment whose nginx has
      // only the pre-existing 'protospacex/' and 'genoprimer/' location blocks will 404 it and
      // the whole control would silently never appear.  'protospacex/variant_sets' is served by
      // the same view under a prefix that is already proxied everywhere, so it is the fallback.
      // Drop it once every deployment has the /api/ block (see deploy/nginx/).
      axios.get('/api/variant_sets', { params })
        .catch(() => axios.get('/protospacex/variant_sets', { params }).then((r) => {
          console.warn('/api/ is not routed to Django on this deployment; served variant sets ' +
                       'via the protospacex/ fallback. Add the location /api/ block, see deploy/nginx/.');
          return r;
        }))
        .then((response) => {
          if (!cancelled) { setVariantSets(response.data.variant_sets || []) }
        })
        .catch(() => {
          if (!cancelled) { setVariantSets([]) }   // none built, or Django is unreachable
        });
      return () => { cancelled = true };
    }, [parentToChildGenome]);

    const handleVariantSourceChange = (event: SelectChangeEvent) => {
      VariantSourceToParent(event.target.value)
    };

    // 2470583 -> "2.47 M".  Variant counts run into the millions and the exact digit is never
    // what the user is deciding on.
    const compact = (n) => {
      if (typeof n !== 'number') { return '\u2014' }
      if (n >= 1e6) { return `${(n / 1e6).toFixed(2)} M` }
      if (n >= 1e3) { return `${(n / 1e3).toFixed(0)} k` }
      return n.toLocaleString();
    };
    const selectedVariantSet = VariantSets.find((vs) => vs.name === parentToChildVariantSource);

    // input genes
    const [Genes, setGenes] = React.useState('');
    const handleGenesChange = (event) => {
      GenesToParent(event.target.value);
      // GenesToParent(event.target.value)
    };

  return (
    <Card variant="outlined" {...other}>
      <CardHeader title={title} subheader={subheader} />

      <Box sx={{ mt:3, ml:3, mr:3, mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'stretch'}}>
      <FormControl  sx={{minWidth: 300, maxWidth: 340 }} dir="ltr">
        <InputLabel id="Genome">Genome</InputLabel>
        <Select
          labelId="Genome"
          id="Genome"
          value={parentToChildGenome}
          onChange={handleGenomeChange}
          // autoWidth
          label="Genome"
        >
          <MenuItem value={"GRCh38"}>Human &nbsp; <font style={{color:'grey', fontSize:"12px"}}>GRCh38 Ensembl</font></MenuItem>
          <MenuItem value={"GRCm39"}>Mouse &nbsp; <font style={{color:'grey', fontSize:"12px"}}>GRCm39 Ensembl</font></MenuItem>
          <MenuItem value={"GRCz11"}>Zebrafish &nbsp; <font style={{color:'grey', fontSize:"12px"}}>GRCz11 Ensembl</font></MenuItem>
          <MenuItem value={"mRatBN7.2"}>Rat &nbsp; <font style={{color:'grey', fontSize:"12px"}}>mRatBN7.2 Ensembl</font></MenuItem>
        </Select>
        <FormHelperText>&nbsp;&nbsp;&nbsp;&nbsp;* Required</FormHelperText>
      </FormControl>

      {/* mt:2, not 1: the label sits in the outline notch, above the box, so it needs
          clearance from the Genome control's helper line */}
      {VariantSets.length > 0 &&
      <FormControl sx={{ minWidth: 300, maxWidth: 340, mt: 2 }} dir="ltr">
        <InputLabel id="VariantSource" shrink>Cell line / variant background</InputLabel>
        <Select
          labelId="VariantSource"
          id="VariantSource"
          value={parentToChildVariantSource || ''}
          onChange={handleVariantSourceChange}
          displayEmpty
          input={<OutlinedInput notched label="Cell line / variant background" />}
        >
          <MenuItem value={""}>None &nbsp; <font style={{color:'grey', fontSize:"12px"}}>reference genome</font></MenuItem>
          {VariantSets.map((vs) => (
            <MenuItem key={vs.name} value={vs.name}>
              {vs.sample} &nbsp; <font style={{color:'grey', fontSize:"12px"}}>hap{vs.haplotype} &middot; {compact(vs.applied)} substitutions</font>
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          {selectedVariantSet
            ? <span>&nbsp;&nbsp;&nbsp;&nbsp;
                {compact(selectedVariantSet.applied)} substitutions applied
                {selectedVariantSet.applied_per_kb ? ` (1 per ${Math.round(1000 / selectedVariantSet.applied_per_kb).toLocaleString()} bp)` : ''}
              </span>
            : <span>&nbsp;&nbsp;&nbsp;&nbsp;Design against this cell line&apos;s genotype</span>}
          <CustomWidthTooltip title={<span>
            <p>Personalized homology arms, guide sequences, recoding and recut CFD to that cell line.</p>
            <p>Guides whose PAM is altered in that cell line are down-weighted (the design can&apos;t change the PAM).</p>
            <p><b>NOTE:</b> Insertions and deletions are not applied, they are reported instead.</p>
            {selectedVariantSet && <p style={{color:'grey'}}>
              {selectedVariantSet.sample} haplotype {selectedVariantSet.haplotype}, called against {selectedVariantSet.base_genome_ver}.
              &nbsp;{compact(selectedVariantSet.applied)} substitutions written into the genome;
              &nbsp;{compact(selectedVariantSet.recorded_not_applied)} further calls recorded but not applied
              ({compact(selectedVariantSet.indels_recorded)} indels, the rest on the other haplotype or unphased);
              &nbsp;{compact(selectedVariantSet.sequence_known_wrong)} windows where the emitted sequence is known
              not to be this haplotype. Source: {selectedVariantSet.source_vcf}
            </p>}
          </span>}>
            <IconButton>
              <InfoOutlinedIcon fontSize="small"/>
            </IconButton>
          </CustomWidthTooltip>
        </FormHelperText>
      </FormControl>
      }

      </Box>


      {/* same sx as the Genome/variant Box above, so the text box tracks the dropdowns'
          width instead of being pinned at a fixed 300 px */}
      <Box sx={{ mt:2, ml:3, mr:3, mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'stretch'}} autoComplete="off">
            <FormControl  sx={{minWidth: 300, maxWidth: 340 }} dir="ltr">
                <TextField 
                    required          
                    multiline
                    rows={5} 
                    value={parentToChildGenes}
                    // defaultValue=""
                    id="outlined-basic" 
                    label="Ensembl transcript ID, terminus or chr:position" 
                    placeholder="ENST00000489081,N&#10;ENST00000453996,C&#10;ENST00000410067,N&#10;ENST00000305321,16:30193791&#10;"
                    variant="outlined"
                    // helperText="or upload a file"
                    onChange={handleGenesChange}
                    />
                <FormHelperText>&nbsp;&nbsp;&nbsp;&nbsp;* Required <br/>
                
                {/* <CustomWidthTooltip  title={<span><p>To apply custom offsets to terminus (e.g. two codons after ATG), following these steps:</p>
                <p>(1) Run the prediction with desired terminus, and download both the submission and the result csv files</p>
                (2) In the results csv file, locate the 'edit_pos' column (genomic coordinate of the desired terminus) and apply desired offsets.<br/>
                (3) Copy the 'edit_pos' (edited) and 'chr' columns to replace the "Coordinate" and "Chromosome" columns respectively in the submission csv file.<br/>
                (4) Clear the contents of the "Terminus" column in the submission csv file, and upload the submission spreadsheet in a new prediction request.<br/>
                </span>}> 
                <IconButton>
                  <InfoOutlinedIcon fontSize="small"/>
                </IconButton>
                </CustomWidthTooltip>Terminus offsets  */}

                </FormHelperText>

            </FormControl>

            {/* <Stack spacing={1} direction="row"  sx={{ mt:1}}  >

                <Button variant="contained" size="small" component="label" endIcon={<DriveFolderUploadIcon />} sx={{ textTransform: 'none' }}>
                  Upload .csv
                  <input hidden accept=".csv" multiple type="file" />
                </Button>

            </Stack> */}
    </Box>





    </Card>
  );
}
